import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { isAfter, startOfDay } from 'date-fns';
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { Box, Checkbox, ConfirmationModal, IconButton, KeyboardDatePicker, MenuItem, Row, Select, Spinner, SplitButton, TextButton, TextField, Tooltip, Typography, useOverlayContext } from 'seagull';
import { Trash } from 'seagull/icons';
import { LinkIcon, QuestionCircle } from 'seagull/icons/essentials';
import { MoneySend } from 'seagull/icons/money';
import { getTestIds } from 'seagull/utils/TestIds';
import { DefaultValuePopover } from '~/components/AgreementStudioPage/TemplateVariables/DefaultValuePopover/DefaultValuePopover';
import { useTextFieldVariableIntegration } from '~/components/AgreementStudioPage/TemplateVariables/hooks/useTextFieldVariableIntegration';
import { TemplateVariablesProvider } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariablesContext';
import { VariableFieldAdornment } from '~/components/AgreementStudioPage/TemplateVariables/VariableFieldAdornment/VariableFieldAdornment';
import AttachFiles from '~/components/AttachFiles/AttachFiles';
import { Attachments } from '~/components/Attachments/Attachments';
import BillChargeTableItem from '~/components/BillClientModal/BillChargeTableItem/BillChargeTableItem';
import { createEmptyChargeItem } from '~/components/BillClientModal/BillClientItem.utils';
import { BillingType, BillMode } from '~/components/BillClientModal/BillClientModal.types';
import { LegalTermsDropdown } from '~/components/LegalTermsDropdown/LegalTermsDropdown';
import { VariablePickerPopover } from '~/components/VariablePickerPopover/VariablePickerPopover';
import { routes } from '~/constants';
import { useStore } from '~/contexts';
import { useLegalTerms } from '~/hooks/reactQuery/queries/useLegalTerms';
import { useRelationship } from '~/hooks/reactQuery/queries/useRelationship';
import { useAttachFiles } from '~/hooks/useAttachFiles';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { ContactModel, ContactAccountantAccess, ContactEditMode, ContactStatus } from '~/models/ContactModel';
import { useIoContext } from '~/providers/IoContext';
import { selectors } from '~/store/state-selectors';
import { connectControllerToKeyboardDatePicker } from '~/utils/react-hook-form.utils';
import styles from './AdHocBillModal.module.scss';
import { AdHocSendOption, mapFormToAdHocSubmitRequest, NetTermsOption, resolveSubmitSemantics } from './AdHocBillModal.types';
import { ScheduleDialog } from './ConfirmationDialogs/ScheduleDialog';
import { SendByEmailDialog } from './ConfirmationDialogs/SendByEmailDialog';
import { ShareByLinkDialog } from './ConfirmationDialogs/ShareByLinkDialog';
import { useAdHocApprovalLink } from './hooks/useAdHocApprovalLink';
import { useAdHocSubmit } from './hooks/useAdHocSubmit';
const paymentSettingsTextsPath = 'vendorOnboarding.adHocBillModal.paymentSettings';
const emailContentTextsPath = 'vendorOnboarding.adHocBillModal.emailContent';
const sendButtonTextsPath = 'vendorOnboarding.adHocBillModal.sendButton';
export const GetAdHocBillModalTID = (parentTestId)=>getTestIds(parentTestId);
const PaymentSettingsSection = (param)=>{
    let { testIds, showTermsOfService, texts } = param;
    const { control, clearErrors, getValues, trigger } = useFormContext();
    return /*#__PURE__*/ _jsxs(Box, {
        className: styles.section,
        testId: testIds.Section(),
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph2",
                weight: "normal",
                className: styles.sectionTitle,
                children: texts.sectionTitle()
            }),
            /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "coverCreditCardFees",
                render: (renderProps)=>/*#__PURE__*/ _jsxs(Select, {
                        bi: {
                            name: 'cc_fee_paid_by'
                        },
                        label: texts.ccFeePaidBy.label(),
                        testId: testIds.CcFeePaidBy(),
                        value: renderProps.field.value ? 'client' : 'vendor',
                        onChange: (event)=>renderProps.field.onChange(event.target.value === 'client'),
                        children: [
                            /*#__PURE__*/ _jsx(MenuItem, {
                                value: "client",
                                testId: "".concat(testIds.CcFeePaidBy(), "-option-client"),
                                children: texts.ccFeePaidBy.theClient()
                            }),
                            /*#__PURE__*/ _jsx(MenuItem, {
                                value: "vendor",
                                testId: "".concat(testIds.CcFeePaidBy(), "-option-vendor"),
                                children: texts.ccFeePaidBy.theVendor()
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "netTerms",
                render: (renderProps)=>/*#__PURE__*/ _jsxs(Select, {
                        bi: {
                            name: 'net_terms'
                        },
                        label: texts.netTerms.label(),
                        testId: testIds.NetTerms(),
                        value: renderProps.field.value,
                        onChange: (event)=>renderProps.field.onChange(event.target.value),
                        children: [
                            /*#__PURE__*/ _jsx(MenuItem, {
                                value: NetTermsOption.Immediate,
                                testId: "".concat(testIds.NetTerms(), "-option-immediate"),
                                children: texts.netTerms.immediate()
                            }),
                            /*#__PURE__*/ _jsx(MenuItem, {
                                value: NetTermsOption.Net7,
                                testId: "".concat(testIds.NetTerms(), "-option-net7"),
                                children: texts.netTerms.net7()
                            }),
                            /*#__PURE__*/ _jsx(MenuItem, {
                                value: NetTermsOption.Net15,
                                testId: "".concat(testIds.NetTerms(), "-option-net15"),
                                children: texts.netTerms.net15()
                            }),
                            /*#__PURE__*/ _jsx(MenuItem, {
                                value: NetTermsOption.Net30,
                                testId: "".concat(testIds.NetTerms(), "-option-net30"),
                                children: texts.netTerms.net30()
                            }),
                            /*#__PURE__*/ _jsx(MenuItem, {
                                value: NetTermsOption.Net45,
                                testId: "".concat(testIds.NetTerms(), "-option-net45"),
                                children: texts.netTerms.net45()
                            }),
                            /*#__PURE__*/ _jsx(MenuItem, {
                                value: NetTermsOption.Net60,
                                testId: "".concat(testIds.NetTerms(), "-option-net60"),
                                children: texts.netTerms.net60()
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "includeTermsOfService",
                render: (renderProps)=>/*#__PURE__*/ _jsxs(Row, {
                        gap: 4,
                        alignItems: "center",
                        children: [
                            /*#__PURE__*/ _jsx(Checkbox, {
                                testId: testIds.IncludeTermsOfService(),
                                checked: renderProps.field.value,
                                onChange: (event)=>{
                                    const checked = event.currentTarget.checked;
                                    renderProps.field.onChange(checked);
                                    if (!checked) clearErrors('termsOfServiceId');
                                },
                                label: texts.includeTermsOfService(),
                                bi: {
                                    name: 'include_terms_of_service'
                                }
                            }),
                            /*#__PURE__*/ _jsx(Tooltip, {
                                title: texts.includeTermsOfServiceTooltip(),
                                placement: "top",
                                children: /*#__PURE__*/ _jsx("span", {
                                    "data-testid": testIds.IncludeTermsOfServiceTooltip(),
                                    className: styles.tooltipIcon,
                                    children: /*#__PURE__*/ _jsx(QuestionCircle, {
                                        size: 16
                                    })
                                })
                            })
                        ]
                    })
            }),
            showTermsOfService && /*#__PURE__*/ _jsx(Box, {
                className: styles.termsOfServiceIndent,
                testId: testIds.TermsOfService(),
                children: /*#__PURE__*/ _jsx(Controller, {
                    control: control,
                    name: "termsOfServiceId",
                    rules: {
                        validate: (value)=>!getValues('includeTermsOfService') || !!value || texts.termsOfService.required()
                    },
                    render: (renderProps)=>{
                        var _renderProps_fieldState_error;
                        return /*#__PURE__*/ _jsx(LegalTermsDropdown, {
                            testId: testIds.TermsOfService(),
                            selectedLegalTermId: renderProps.field.value || undefined,
                            onSelectLegalTerm: (legalTerm)=>{
                                var _ref;
                                renderProps.field.onChange((_ref = legalTerm === null || legalTerm === void 0 ? void 0 : legalTerm.id) !== null && _ref !== void 0 ? _ref : '');
                                trigger('termsOfServiceId');
                            },
                            error: (_renderProps_fieldState_error = renderProps.fieldState.error) === null || _renderProps_fieldState_error === void 0 ? void 0 : _renderProps_fieldState_error.message
                        });
                    }
                })
            })
        ]
    });
};
const EmailContentSection = (param)=>{
    let { testIds, texts } = param;
    const { control, setValue } = useFormContext();
    const subjectInputRef = useRef(null);
    const contentInputRef = useRef(null);
    const setSubjectField = useCallback((newValue)=>setValue('emailSubject', newValue), [
        setValue
    ]);
    const setContentField = useCallback((newValue)=>setValue('emailContent', newValue), [
        setValue
    ]);
    const subjectIntegration = useTextFieldVariableIntegration(subjectInputRef, setSubjectField);
    const contentIntegration = useTextFieldVariableIntegration(contentInputRef, setContentField, {
        adornmentStyle: {
            position: 'absolute',
            top: 14,
            right: 14
        }
    });
    return /*#__PURE__*/ _jsxs(Box, {
        className: styles.section,
        testId: testIds.Section(),
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph2",
                weight: "normal",
                className: styles.sectionTitle,
                children: texts.sectionTitle()
            }),
            /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "emailSubject",
                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, {
                        bi: {
                            name: 'email_subject'
                        },
                        label: texts.subjectLabel(),
                        testId: testIds.Subject(),
                        value: renderProps.field.value,
                        onChange: renderProps.field.onChange,
                        onBlur: renderProps.field.onBlur,
                        inputRef: (el)=>{
                            subjectInputRef.current = el;
                        },
                        inputProps: subjectIntegration.inputProps,
                        InputProps: subjectIntegration.enabled ? {
                            endAdornment: /*#__PURE__*/ _jsx("span", {
                                ref: subjectIntegration.adornmentRef,
                                style: subjectIntegration.adornmentStyle,
                                children: /*#__PURE__*/ _jsx(VariableFieldAdornment, _object_spread({}, subjectIntegration.fieldAdornmentProps))
                            })
                        } : undefined
                    })
            }),
            /*#__PURE__*/ _jsx(Controller, {
                control: control,
                name: "emailContent",
                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, {
                        bi: {
                            name: 'email_content'
                        },
                        multiline: true,
                        minRows: 6,
                        className: styles.emailContentField,
                        label: texts.contentLabel(),
                        testId: testIds.Content(),
                        value: renderProps.field.value,
                        onChange: renderProps.field.onChange,
                        onBlur: renderProps.field.onBlur,
                        inputRef: (el)=>{
                            contentInputRef.current = el;
                        },
                        inputProps: contentIntegration.inputProps,
                        InputProps: contentIntegration.enabled ? {
                            endAdornment: /*#__PURE__*/ _jsx("span", {
                                ref: contentIntegration.adornmentRef,
                                style: contentIntegration.adornmentStyle,
                                children: /*#__PURE__*/ _jsx(VariableFieldAdornment, _object_spread({}, contentIntegration.fieldAdornmentProps))
                            })
                        } : undefined
                    })
            }),
            subjectIntegration.enabled && /*#__PURE__*/ _jsx(VariablePickerPopover, _object_spread({}, subjectIntegration.pickerPopoverProps)),
            subjectIntegration.enabled && /*#__PURE__*/ _jsx(DefaultValuePopover, _object_spread({}, subjectIntegration.defaultValuePopoverProps)),
            contentIntegration.enabled && /*#__PURE__*/ _jsx(VariablePickerPopover, _object_spread({}, contentIntegration.pickerPopoverProps)),
            contentIntegration.enabled && /*#__PURE__*/ _jsx(DefaultValuePopover, _object_spread({}, contentIntegration.defaultValuePopoverProps))
        ]
    });
};
const SendSplitButtonControl = (param)=>{
    let { testIds, isFutureIssueDate, onSelect, texts } = param;
    return /*#__PURE__*/ _jsxs(SplitButton, {
        label: texts.label(),
        testId: testIds.SendSplitButton(),
        onClick: ()=>onSelect(AdHocSendOption.SendByEmail),
        children: [
            /*#__PURE__*/ _jsx(MenuItem, {
                testId: testIds.SendByEmailOption(),
                onClick: ()=>onSelect(AdHocSendOption.SendByEmail),
                children: /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    children: [
                        /*#__PURE__*/ _jsx(MoneySend, {
                            size: 16
                        }),
                        texts.sendByEmail()
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(MenuItem, {
                testId: testIds.ShareByLinkOption(),
                disabled: isFutureIssueDate,
                tooltipProps: isFutureIssueDate ? {
                    title: texts.shareByLinkDisabledTooltip(),
                    placement: 'top'
                } : undefined,
                onClick: ()=>onSelect(AdHocSendOption.ShareByLink),
                children: /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    children: [
                        /*#__PURE__*/ _jsx(LinkIcon, {
                            size: 16
                        }),
                        texts.shareByLink()
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(MenuItem, {
                testId: testIds.MarkPaidExternallyOption(),
                disabled: isFutureIssueDate,
                tooltipProps: isFutureIssueDate ? {
                    title: texts.markPaidExternallyDisabledTooltip(),
                    placement: 'top'
                } : undefined,
                onClick: ()=>onSelect(AdHocSendOption.MarkPaidExternally),
                children: /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    children: [
                        /*#__PURE__*/ _jsx(MoneySend, {
                            size: 16
                        }),
                        texts.markPaidExternally()
                    ]
                })
            })
        ]
    });
};
const buildClientContact = (relationship)=>{
    var _relationship_clientContactId, _relationship_buyer_firstName, _relationship_buyer_lastName;
    return new ContactModel({
        id: (_relationship_clientContactId = relationship.clientContactId) !== null && _relationship_clientContactId !== void 0 ? _relationship_clientContactId : relationship.buyer.id,
        createdAt: 0,
        userId: null,
        firstName: (_relationship_buyer_firstName = relationship.buyer.firstName) !== null && _relationship_buyer_firstName !== void 0 ? _relationship_buyer_firstName : '',
        lastName: (_relationship_buyer_lastName = relationship.buyer.lastName) !== null && _relationship_buyer_lastName !== void 0 ? _relationship_buyer_lastName : '',
        companyName: relationship.customer.name,
        email: relationship.buyer.email,
        phone: relationship.buyer.phone,
        isSample: false,
        edit: {
            mode: ContactEditMode.Readonly
        },
        vendorId: relationship.vendor.id,
        version: '',
        accountantAccess: ContactAccountantAccess.NonCandidate,
        status: ContactStatus.Active,
        hasSignedAgreements: false,
        hasPendingProposals: false,
        integrations: [],
        archiveEligibility: {
            type: 'eligible'
        },
        deleteEligibility: {
            type: 'eligible'
        },
        hasDrafts: false
    });
};
const AdHocBillModalContent = (props)=>{
    var _useStore;
    const { AdHocBillModalTID } = useMemo(()=>GetAdHocBillModalTID(), []);
    const { relationship } = props;
    const { t: globalBillTexts } = useLocaleKeys('components.globalBillModal');
    const { t: adHocTexts } = useLocaleKeys('vendorOnboarding.adHocBillModal');
    const { t: paymentSettingsTexts } = useLocaleKeys(paymentSettingsTextsPath);
    const { t: emailContentTexts } = useLocaleKeys(emailContentTextsPath);
    const { t: sendButtonTexts } = useLocaleKeys(sendButtonTextsPath);
    const { t: commonTexts } = useLocaleKeys('common');
    const businessName = (_useStore = useStore(selectors.user.businessName)) !== null && _useStore !== void 0 ? _useStore : '';
    const worksAt = useStore(selectors.user.business);
    const { getNow } = useIoContext();
    const { attachments, openFilePicker, isUploading, handleRemove } = useAttachFiles();
    const { legalTerms } = useLegalTerms();
    const { submitAdHoc } = useAdHocSubmit();
    const { startListening, resolveApprovalLink } = useAdHocApprovalLink();
    const { showErrorToast, showSuccessToast, showModal } = useOverlayContext();
    const history = useHistory();
    const contact = useMemo(()=>buildClientContact(relationship), [
        relationship
    ]);
    const form = useForm({
        mode: 'onSubmit',
        defaultValues: {
            mode: BillMode.Now,
            issueDate: getNow(),
            items: [
                createEmptyChargeItem()
            ],
            coverCreditCardFees: true,
            netTerms: NetTermsOption.Immediate,
            includeTermsOfService: false,
            termsOfServiceId: '',
            emailSubject: emailContentTexts.defaultSubject({
                businessName
            }),
            emailContent: emailContentTexts.defaultContent()
        }
    });
    const itemsControl = useFieldArray({
        control: form.control,
        name: 'items'
    });
    const [items, includeTermsOfService, issueDate, coverCreditCardFees, emailSubject, emailContent] = form.watch([
        'items',
        'includeTermsOfService',
        'issueDate',
        'coverCreditCardFees',
        'emailSubject',
        'emailContent'
    ]);
    const templateVarTextFields = useMemo(()=>[
            emailSubject,
            emailContent
        ], [
        emailSubject,
        emailContent
    ]);
    const templateVarRichTextFields = useMemo(()=>[], []);
    const isFutureIssueDate = isAfter(startOfDay(issueDate), startOfDay(getNow()));
    const buyerContactName = [
        relationship.buyer.firstName,
        relationship.buyer.lastName
    ].filter((part)=>part != null && part.length > 0).join(' ');
    const onAddOutOfScopeCharge = ()=>{
        itemsControl.append(createEmptyChargeItem());
    };
    const handleDeleteItem = (itemIndex)=>{
        itemsControl.remove(itemIndex);
    };
    const navigateAfterSubmit = ()=>{
        history.push(routes.invoices(props.relationshipId));
    };
    const showSuccessToastForOption = (option, scheduledForFuture)=>{
        switch(option){
            case AdHocSendOption.SendByEmail:
                if (scheduledForFuture) {
                    showSuccessToast(commonTexts.toasts.chargesSuccessfullyScheduled(), 'ad-hoc-bill-schedule-success-toast');
                } else {
                    showSuccessToast(commonTexts.toasts.chargesSuccessfullyCreated(), 'ad-hoc-bill-send-success-toast');
                }
                return;
            case AdHocSendOption.MarkPaidExternally:
                showSuccessToast(commonTexts.toasts.invoiceIssuedAndMarkedAsExternallyPaid(), 'ad-hoc-bill-paid-externally-success-toast');
                return;
            case AdHocSendOption.ShareByLink:
                return;
        }
    };
    const submit = async (option, data, onSuccess)=>{
        var _legalTerms_find;
        const scheduledForFuture = isAfter(startOfDay(data.issueDate), startOfDay(getNow()));
        const semantics = resolveSubmitSemantics({
            option,
            isFutureIssueDate: scheduledForFuture
        });
        const selectedLegalTerm = (_legalTerms_find = legalTerms.find((lt)=>lt.id === data.termsOfServiceId)) !== null && _legalTerms_find !== void 0 ? _legalTerms_find : null;
        const request = mapFormToAdHocSubmitRequest({
            form: data,
            semantics,
            attachments: attachments.map((att)=>({
                    id: att.id,
                    displayName: att.displayName
                })),
            selectedLegalTerm,
            defaultEmailSubject: emailContentTexts.defaultSubject({
                businessName
            }),
            defaultEmailContent: emailContentTexts.defaultContent()
        });
        const { success } = await handleRequest(submitAdHoc({
            relationshipId: props.relationshipId,
            data: request
        }));
        if (!success) {
            showErrorToast(adHocTexts.submitError(), 'ad-hoc-submit-error-toast');
            return false;
        }
        onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess();
        showSuccessToastForOption(option, scheduledForFuture);
        navigateAfterSubmit();
        props.dismissModal();
        return true;
    };
    const openSendDialog = ()=>{
        const DialogComponent = isFutureIssueDate ? ScheduleDialog : SendByEmailDialog;
        showModal({
            biName: 'dialog_component_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal: dismissDialog } = param;
                return /*#__PURE__*/ _jsx(DialogComponent, {
                    companyName: relationship.customer.name,
                    contactName: buyerContactName,
                    email: relationship.buyer.email,
                    coverCreditCardFees: coverCreditCardFees,
                    onBack: dismissDialog,
                    onConfirm: ()=>submit(AdHocSendOption.SendByEmail, form.getValues(), dismissDialog)
                });
            }
        });
    };
    const openShareByLinkDialog = ()=>{
        showModal({
            biName: 'share_by_link_dialog_modal',
            closeOnClickOutside: false,
            closeOnEscapeKey: false,
            content: (param)=>{
                let { dismissModal: dismissDialog } = param;
                return /*#__PURE__*/ _jsx(ShareByLinkDialog, {
                    companyName: relationship.customer.name,
                    contactName: buyerContactName,
                    email: relationship.buyer.email,
                    onCancel: dismissDialog,
                    onGenerateLink: async ()=>{
                        var _legalTerms_find;
                        const invoiceIdPromise = startListening(props.relationshipId);
                        const data = form.getValues();
                        const semantics = resolveSubmitSemantics({
                            option: AdHocSendOption.ShareByLink,
                            isFutureIssueDate
                        });
                        const selectedLegalTerm = (_legalTerms_find = legalTerms.find((lt)=>lt.id === data.termsOfServiceId)) !== null && _legalTerms_find !== void 0 ? _legalTerms_find : null;
                        const request = mapFormToAdHocSubmitRequest({
                            form: data,
                            semantics,
                            attachments: attachments.map((att)=>({
                                    id: att.id,
                                    displayName: att.displayName
                                })),
                            selectedLegalTerm,
                            defaultEmailSubject: emailContentTexts.defaultSubject({
                                businessName
                            }),
                            defaultEmailContent: emailContentTexts.defaultContent()
                        });
                        const { success } = await handleRequest(submitAdHoc({
                            relationshipId: props.relationshipId,
                            data: request
                        }));
                        if (!success) {
                            showErrorToast(adHocTexts.submitError(), 'ad-hoc-submit-error-toast');
                            return {
                                success: false
                            };
                        }
                        try {
                            const linkUrl = await resolveApprovalLink(invoiceIdPromise);
                            return {
                                success: true,
                                linkUrl
                            };
                        } catch (unused) {
                            return {
                                success: true,
                                linkUrl: null
                            };
                        }
                    },
                    onClose: ()=>{
                        dismissDialog();
                        showSuccessToast(commonTexts.toasts.chargesSuccessfullyCreated(), 'ad-hoc-bill-share-link-success-toast');
                        history.push(routes.billingHub(props.relationshipId));
                        props.dismissModal();
                    }
                });
            }
        });
    };
    const handleSendOption = (option)=>{
        if (option === AdHocSendOption.SendByEmail) {
            form.handleSubmit(()=>openSendDialog())();
            return;
        }
        if (option === AdHocSendOption.ShareByLink) {
            form.handleSubmit(()=>openShareByLinkDialog())();
            return;
        }
        form.handleSubmit((data)=>submit(option, data))();
    };
    const renderTableItem = (item, itemIndex)=>{
        if (item.type !== BillingType.Charge) return null;
        return /*#__PURE__*/ _jsx(BillChargeTableItem, {
            itemIndex: itemIndex,
            chargeItem: item,
            relationship: relationship,
            hideApprovalCallout: true,
            actionColumn: items.length > 1 ? /*#__PURE__*/ _jsx(IconButton, {
                bi: {
                    name: 'delete_ad_hoc_bill_item'
                },
                testId: AdHocBillModalTID.DeleteItem(itemIndex),
                onClick: ()=>handleDeleteItem(itemIndex),
                children: /*#__PURE__*/ _jsx(Trash, {
                    size: 20
                })
            }) : null
        }, item.itemId);
    };
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(ConfirmationModal, {
            bi: {
                primaryButton: {
                    name: 'ad_hoc_bill_1_confirm'
                },
                secondaryButton: {
                    name: 'ad_hoc_bill_1_cancel'
                }
            },
            title: globalBillTexts.billClientTitle({
                customerName: relationship.customer.name
            }),
            width: "1600px",
            testId: AdHocBillModalTID.Root(),
            textAlignCenter: false,
            onCloseButtonClick: props.dismissModal,
            content: worksAt == null ? /*#__PURE__*/ _jsx(Box, {
                className: styles.loadingWrapper,
                testId: AdHocBillModalTID.LoadingSpinner(),
                children: /*#__PURE__*/ _jsx(Spinner, {
                    size: 48
                })
            }) : /*#__PURE__*/ _jsx(TemplateVariablesProvider, {
                contact: contact,
                worksAt: worksAt,
                agreementName: null,
                effectiveDate: null,
                initialTemplateVariableValues: undefined,
                textFields: templateVarTextFields,
                richTextFields: templateVarRichTextFields,
                children: /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, form), {
                    children: /*#__PURE__*/ _jsxs(Box, {
                        className: styles.modalContentWrapper,
                        children: [
                            /*#__PURE__*/ _jsxs(Box, {
                                className: styles.headerRow,
                                children: [
                                    /*#__PURE__*/ _jsx(Controller, {
                                        control: form.control,
                                        name: "issueDate",
                                        rules: requiredValidation(true),
                                        render: (renderProps)=>/*#__PURE__*/ _jsx(KeyboardDatePicker, _object_spread({
                                                bi: {
                                                    name: 'ad_hoc_issue_date'
                                                },
                                                testId: AdHocBillModalTID.IssueDate(),
                                                label: adHocTexts.issueDateLabel()
                                            }, connectControllerToKeyboardDatePicker(renderProps)))
                                    }),
                                    /*#__PURE__*/ _jsx(AttachFiles, {
                                        attachments: attachments,
                                        openFilePicker: openFilePicker,
                                        isUploading: isUploading
                                    })
                                ]
                            }),
                            items.map(renderTableItem),
                            /*#__PURE__*/ _jsx(TextButton, {
                                testId: AdHocBillModalTID.AddCharge(),
                                underline: "none",
                                weight: "light",
                                onClick: onAddOutOfScopeCharge,
                                children: adHocTexts.addCharge()
                            }),
                            /*#__PURE__*/ _jsxs(Box, {
                                className: styles.sectionsRow,
                                children: [
                                    /*#__PURE__*/ _jsx(PaymentSettingsSection, {
                                        testIds: AdHocBillModalTID.PaymentSettings,
                                        showTermsOfService: includeTermsOfService,
                                        texts: paymentSettingsTexts
                                    }),
                                    /*#__PURE__*/ _jsx(EmailContentSection, {
                                        testIds: AdHocBillModalTID.EmailContent,
                                        texts: emailContentTexts
                                    })
                                ]
                            }),
                            attachments.length > 0 && /*#__PURE__*/ _jsx(Attachments, {
                                files: attachments,
                                onRemove: handleRemove,
                                disableDownloadLinks: true
                            }),
                            /*#__PURE__*/ _jsx(Box, {
                                className: styles.sendButtonRow,
                                children: /*#__PURE__*/ _jsx(SendSplitButtonControl, {
                                    testIds: AdHocBillModalTID,
                                    isFutureIssueDate: isFutureIssueDate,
                                    onSelect: handleSendOption,
                                    texts: sendButtonTexts
                                })
                            })
                        ]
                    })
                }))
            })
        })
    });
};
export const AdHocBillModal = (props)=>{
    const { AdHocBillModalTID } = useMemo(()=>GetAdHocBillModalTID(), []);
    const { t: adHocTexts } = useLocaleKeys('vendorOnboarding.adHocBillModal');
    const { relationship } = useRelationship(props.relationshipId);
    if (relationship == null) {
        return /*#__PURE__*/ _jsx(ConfirmationModal, {
            bi: {
                primaryButton: {
                    name: 'ad_hoc_bill_2_confirm'
                },
                secondaryButton: {
                    name: 'ad_hoc_bill_2_cancel'
                }
            },
            testId: AdHocBillModalTID.Root(),
            title: adHocTexts.title(),
            width: "1600px",
            textAlignCenter: false,
            onCloseButtonClick: props.dismissModal,
            content: /*#__PURE__*/ _jsx(Box, {
                className: styles.loadingWrapper,
                testId: AdHocBillModalTID.LoadingSpinner(),
                children: /*#__PURE__*/ _jsx(Spinner, {
                    size: 48
                })
            })
        });
    }
    return /*#__PURE__*/ _jsx(AdHocBillModalContent, _object_spread_props(_object_spread({}, props), {
        relationship: relationship
    }));
};
